import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus } from "lucide-react";
import { volunteerQuals } from "@/lib/quals";

const fmtDate = (d) => new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export function eligible(p, slot, svc, vehicleId) {
  const q = p.qualifications || [];
  if (slot === "ambulanza") return q.includes("Autista");
  if (slot === "mezzo") {
    const v = (svc.required_vehicles || []).find((x) => x.id === vehicleId);
    return !v?.requires_autista || q.includes("Autista");
  }
  if (slot === "medico") return q.includes("Medico");
  if (slot === "infermiere") return q.includes("Infermiere");
  return volunteerQuals(svc.required_qualifications).filter((x) => x !== "Autista").every((x) => q.includes(x));
}

export function ServiceAssign({ svc, onChanged }) {
  const [people, setPeople] = useState([]);
  const [slot, setSlot] = useState("volontario");
  const [person, setPerson] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [dates, setDates] = useState([]);
  const [busy, setBusy] = useState(false);
  const svcDates = svc.dates || [];

  useEffect(() => {
    api.get("/volunteers").then(({ data }) => setPeople(data)).catch(() => {});
  }, []);
  useEffect(() => { setPerson(""); }, [slot, vehicle]);

  const slots = [
    { v: "volontario", l: "Volontario" },
    svc.required_ambulances > 0 && { v: "ambulanza", l: "Autista ambulanza" },
    (svc.required_vehicles || []).length > 0 && { v: "mezzo", l: "Autista mezzo" },
    svc.required_medici > 0 && { v: "medico", l: "Medico" },
    svc.required_infermieri > 0 && { v: "infermiere", l: "Infermiere" },
  ].filter(Boolean);
  const assigned = new Set((svc.signups || []).map((s) => s.user_id));
  const options = people.filter((p) => !assigned.has(p.id) && eligible(p, slot, svc, vehicle));
  const freeVehicles = (svc.required_vehicles || []).filter((v) => !v.driver_id);

  const assign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/services/${svc.id}/assign`, { user_id: person, slot, dates, vehicle_id: slot === "mezzo" ? vehicle : null });
      toast.success("Inserito nel servizio");
      setPerson(""); setDates([]); setVehicle("");
      onChanged(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid="service-assign">
      <p className="text-sm font-semibold flex items-center gap-1.5"><UserPlus className="h-4 w-4 text-[#D32F2F]" /> Inserisci direttamente</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <Select value={slot} onValueChange={setSlot}>
          <SelectTrigger className="h-8 text-xs" data-testid="assign-slot"><SelectValue /></SelectTrigger>
          <SelectContent>{slots.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        {slot === "mezzo" && (
          <Select value={vehicle} onValueChange={setVehicle}>
            <SelectTrigger className="h-8 text-xs" data-testid="assign-vehicle"><SelectValue placeholder={freeVehicles.length ? "Scegli il mezzo…" : "Mezzi già assegnati"} /></SelectTrigger>
            <SelectContent>{freeVehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={person} onValueChange={setPerson} disabled={slot === "mezzo" && !vehicle}>
          <SelectTrigger className="h-8 text-xs sm:col-span-2" data-testid="assign-person"><SelectValue placeholder={options.length ? "Scegli chi inserire…" : "Nessuno con le qualifiche richieste"} /></SelectTrigger>
          <SelectContent>{options.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {svcDates.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="assign-dates">
          <button type="button" data-testid="assign-all-dates" onClick={() => setDates(dates.length === svcDates.length ? [] : svcDates)}
            className="text-xs rounded-full px-2 py-1 border border-[#D32F2F]/40 text-[#D32F2F] hover:bg-[#D32F2F]/10 transition-colors">
            {dates.length === svcDates.length ? "Nessuna" : "Tutte le date"}
          </button>
          {svcDates.map((d) => (
            <label key={d} className="flex items-center gap-1.5 text-xs border border-border rounded-full px-2 py-1 cursor-pointer">
              <Checkbox checked={dates.includes(d)} onCheckedChange={() => setDates((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d]))} />
              {fmtDate(d)}
            </label>
          ))}
        </div>
      )}
      <Button size="sm" data-testid="assign-submit" disabled={busy || !person || (svcDates.length > 1 && dates.length === 0)} onClick={assign} className="rounded-full h-8">
        Inserisci
      </Button>
    </div>
  );
}
